import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  clampZoom,
  getNextZoomLevel,
  loadZoomFromStorage,
  saveZoomToStorage,
  type SetZoomLevelOptions,
  ZOOM_CONFIG,
  ZoomContext,
  type ZoomContextType,
} from 'context/ZoomContext';
import type { ZoomProviderProps } from './ZoomProvider.props';

const ZOOM_ROOT_SELECTOR = '#root';

const applyRootZoom = (zoomLevel: number) => {
  const root = document.querySelector<HTMLElement>(ZOOM_ROOT_SELECTOR);
  if (root) {
    root.style.zoom = zoomLevel.toString();
  }
};

export const ZoomProvider = ({ children }: ZoomProviderProps) => {
  const [zoomLevel, setZoomLevelState] = useState<number>(loadZoomFromStorage);

  useEffect(() => {
    applyRootZoom(zoomLevel);
  }, [zoomLevel]);

  const setZoomLevelWithValidation = useCallback((level: number, options: SetZoomLevelOptions = {}): void => {
    const clampedLevel = clampZoom(level);
    setZoomLevelState(clampedLevel);

    if (options.persist !== false) {
      saveZoomToStorage(clampedLevel);
    }
  }, []);

  useEffect(() => {
    const handleWheelZoom = (event: WheelEvent) => {
      if (event.ctrlKey) {
        event.preventDefault();
        setZoomLevelWithValidation(getNextZoomLevel(zoomLevel, event.deltaY < 0 ? 'in' : 'out'));
      }
    };

    window.addEventListener('wheel', handleWheelZoom, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheelZoom);
    };
  }, [zoomLevel, setZoomLevelWithValidation]);

  const resetZoom = useCallback((): void => {
    setZoomLevelWithValidation(ZOOM_CONFIG.DEFAULT);
  }, [setZoomLevelWithValidation]);

  const zoomIn = useCallback((): void => {
    setZoomLevelWithValidation(getNextZoomLevel(zoomLevel, 'in'));
  }, [setZoomLevelWithValidation, zoomLevel]);

  const zoomOut = useCallback((): void => {
    setZoomLevelWithValidation(getNextZoomLevel(zoomLevel, 'out'));
  }, [zoomLevel, setZoomLevelWithValidation]);

  const getZoomPercentage = useCallback((): string => `${Math.round(zoomLevel * 100)}%`, [zoomLevel]);

  const contextValue: ZoomContextType = useMemo(() => ({
    zoomLevel,
    setZoomLevel: setZoomLevelWithValidation,
    resetZoom,
    zoomIn,
    zoomOut,
    getZoomPercentage,
  }), [zoomLevel, setZoomLevelWithValidation, resetZoom, zoomIn, zoomOut, getZoomPercentage]);

  return <ZoomContext.Provider value={contextValue}>{children}</ZoomContext.Provider>;
};

export default ZoomProvider;
